import React from 'react';
import {
  ControlIcon,
  DownloadIcon,
  ForkIcon,
  IconButton,
  InboxIcon,
  ManualIcon,
  Pane,
  Position,
  SearchIcon,
  Select,
  Text,
  Tooltip,
} from 'evergreen-ui';
import {StartEndDate} from 'Shared';
import {useUrlParams} from 'Utils/useUrlParams';
import CheckboxList from 'Shared/CheckboxList';
import { useGetProgramsQuery } from 'Services/api/programApi';
import { useGetReportFormatsQuery } from 'Services/api/reportFormatsApi';
import { useGetChannelsQuery } from 'Services/api/channelMessagesApi';
import { CHANNEL, INCOMING, CUT, OUTGOING } from 'Features/Stations/Station/Messages/messageUtils';
import { SearchingOnStationAndDatasource } from 'Utils/SearchingOnStationAndDatasource';
import {useGetStationsQuery} from 'Services/api/stantionsApi';
import {getMessagesByIds} from "../../../../Services/api/messageApi";
import {timeZoneToUTC} from "../../../../Utils/UTCConverter";
import {DATE_FORMATS} from "../../../../Utils/formats";
import {set} from "date-fns";
import { datasourcesSelector, stationsFullSelector } from 'Features/App/Dictionaries/selectors';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

const pageSizes = [10, 20, 50, 100];

export default function MessagesPanel({ params, type, toggleView = undefined, useGetStatusQuery = () => [], isArchive, useGetMessages}) {
  const { datasourceId } = useParams();
  const requestParams = datasourceId ? { datasourceId } : undefined;
  const { data: statuses = [] } = useGetStatusQuery();
  const { data: programs = [] } = useGetProgramsQuery(requestParams, {
    skip: type === INCOMING || type === OUTGOING || type === CHANNEL,
  });
  const { data: channels = [] } = useGetChannelsQuery(undefined, { skip: type === CUT || type === CHANNEL });
  const { data: formats = [] } = useGetReportFormatsQuery(undefined, {
    skip: type === INCOMING || type === OUTGOING || type === CHANNEL,
  });
  const stations = useSelector(stationsFullSelector);
  const datasources = useSelector(datasourcesSelector);

  const [, updateParams] = useUrlParams();
  const [date, setDate] = React.useState({
    start: '',
    end: '',
  });
  const [size, setSize] = React.useState(50);

  const [selectedStatuses, setSelectedStatuses] = React.useState([]);
  const [selectedChannels, setSelectedChannels] = React.useState([]);
  const [selectedPrograms, setSelectedPrograms] = React.useState([]);
  const [selectedFormats, setSelectedFormats] = React.useState([]);
  const [selectedStation, setSelectedStation] = React.useState(null);
  const [selectedDatasource, setSelectedDatasource] = React.useState(null);

  const [getMessages, { data: { content: messages = [], totalPages } = {}, isFetching: isLoadingMessages }] = useGetMessages();

  const onChangeDate = (obj) => {
    setDate((cur) => ({
      ...cur,
      [obj.name]: obj.value,
    }));
  };

  const onChangeSize = (e) => {
    const { value } = e.target;
    setSize(parseInt(value, 10));
  };

  const onDownloadClick = () => {
    let messagesIds = [messages.length];
    for (let i = 0; i < messages.length; i++) {
      messagesIds[i] = messages[i].id
    }

    let station = findStationByDatasourceId()
    let name = generateZipName(station, date)
    getMessagesByIds(messagesIds).then((res) => {
      const  blob = new Blob([new Uint8Array(res.data)], {type: "application/zip"});
      const url  = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    })
  };

  /**
   * Поиск станции осуществлен именно по источнику,
   * чтобы не тянуть издалека информацию о станции
   * @returns станция
   */
  function findStationByDatasourceId() {
    let id = params.datasourceIds[0]
    for (let i = 0; i < stations.length; i++) {
      let datasource = stations[i].datasources
      for (let datasourceOrderNum = 0; datasourceOrderNum < datasource.length; datasourceOrderNum++) {

        if (parseInt(id) === parseInt(datasource[datasourceOrderNum].id)) {
          return stations[i];
        }
      }
    }
    return "";
  }

  /**
   * По станции и дате формируем название .zip файла
   * @param station - станция
   * @param date - даты (и начало, и конец)
   * @returns
   */
  function generateZipName(station, date) {
    if (station === "") return "_.zip"

    let nameCore = "messages"
    let beginDate = "с"
    let endDate = "по"
    let divider = "_"
    let extension = ".zip"

    let idx = station.idx
    let stationName = station.name
    let start = date.start.split("T")[0]
    let end = date.end.split("T")[0]

    return idx + divider
      + stationName + divider
      + nameCore + divider
      + beginDate + divider
      + start + divider
      + endDate + divider
      + end + divider
      + extension
  }
  const onSearchClick = () => {
    updateParams({
      startDate: date.start,
      endDate: date.end,
      size,
      status: selectedStatuses.map((s) => s.value),
      programs: selectedPrograms.map((p) => p.value),
      formats: selectedFormats.map((f) => f.value),
      channels: selectedChannels.map((c) => c.value),
      ...(selectedStation ? { station: selectedStation.station.id } : { station: [] }),
      ...(selectedDatasource ? { datasource: selectedDatasource.id } : { datasource: [] }),
    });
  };

  const fetchParams = React.useMemo(() => ({
    startDate: timeZoneToUTC(new Date(params.date.startDate), params.timeZone, DATE_FORMATS.api),
    endDate: timeZoneToUTC(set(new Date(params.date.endDate), { seconds: 59 }), params.timeZone, DATE_FORMATS.api),
    page: params.page,
    size: params.size,
    ...(params.stationId ? { stationId: params.stationId } : {}),
    ...(params.datasourceIds?.length ? { datasourceIds: params.datasourceIds } : {}),
    ...(params.programs.length ? { programIds: params.programs } : {}),
    ...(params.formats.length ? { formatIds: params.formats } : {}),
    ...(params.isStationMessages && params.status.length ? { statusIds: params.status } : {}),
    ...(params.channels.length ? { channelIds: params.channels } : {}),
    ...(params.status.length ? { statusIds: params.status } : {}),
    ...(params.station ? { stationIds: [params.station] } : {}),
    ...(params.datasource ? { datasourceIds: [params.datasource] } : {}),
  }), [params.channels, params.datasource, params.datasourceIds, params.date.endDate, params.date.startDate, params.formats, params.isStationMessages, params.page, params.programs, params.size, params.station, params.stationId, params.status, params.timeZone]);

  React.useEffect(() => {
    getMessages(fetchParams);
  }, [fetchParams, getMessages]);

  React.useEffect(() => {
    if (params.station && stations && type === CUT) {
      const find = stations[params.station]; // stations.find((station) => station.id === params.station);
      if (find) {
        setSelectedStation(find);
        if (params.datasource) {
          const findDatasource = datasources[params.datasource]; // find.datasources.find((d) => d.id === params.datasource);
          if (findDatasource) {
            setSelectedDatasource(findDatasource);
          }
        }
      } else {
        console.error('station not found: ', params.station);
      }
    }
  }, [datasources, params, stations, type]);

  React.useEffect(() => {
    if (params.formats.length && type === CUT) {
      const formatFormats = [];
      params.formats.forEach((id) => {
        const find = formats.find((format) => format.id === id);
        if (!find) {
          console.error('format not found: ', id);
        } else {
          formatFormats.push({
            label: find.description,
            value: find.id,
          });
        }
      });
      setSelectedFormats(formatFormats);
    }
  }, [formats, params.formats, type]);

  React.useEffect(() => {
    if (params.status.length && statuses.length) {
      const formatStatuses = [];
      params.status.forEach((id) => {
        const find = statuses.find((status) => status.id === id);
        if (!find) {
          console.error('status not found: ', id);
        } else {
          formatStatuses.push({
            label: find.status,
            value: find.id,
          });
        }
      });
      setSelectedStatuses(formatStatuses);
    }
  }, [params.status, params.status.length, statuses, updateParams]);

  React.useEffect(() => {
    if (params.programs.length && type === CUT) {
      const formatProgram = [];
      params.programs.forEach((id) => {
        const find = programs.find((p) => p.id === id);
        if (find) {
          formatProgram.push({
            label: find.name,
            value: find.id,
          });
        }
      });
      setSelectedPrograms(formatProgram);
    }
  }, [params.programs, params.programs.length, programs, type]);

  React.useEffect(() => {
    if (params.channels.length && type === INCOMING) {
      const formatChannels = [];
      params.channels.forEach((id) => {
        const find = channels.find((c) => c.id === id);
        if (find) {
          formatChannels.push({
            label: find.description,
            value: find.id,
          });
        }
      });
      setSelectedChannels(formatChannels);
    }
  }, [channels, params.channels, type]);

  React.useEffect(() => {
    setDate({
      start: params.date.startDate,
      end: params.date.endDate,
    });
  }, [params.date.startDate, params.date.endDate]);

  React.useEffect(() => {
    setSize(params.size);
  }, [params.size]);

  return (
    <Pane display="grid" gridTemplateRows="max-content 1fr">
      <Pane display="grid" gridAutoFlow="column" gridAutoColumns="max-content" justifyContent="center" alignItems="center" columnGap=".5rem">
        <Text>Сообщения</Text>
        <StartEndDate startDate={date.start} endDate={date.end} updateDate={onChangeDate} />
        <Text>Показывать по</Text>
        <Select value={size} onChange={onChangeSize}>
          {React.Children.toArray(pageSizes.map((pageSize) => (
            <option value={pageSize}>{pageSize}</option>
          )))}
        </Select>
        {!params.isStationMessages && (
          <CheckboxList
            appearance="default"
            intent="none"
            options={statuses.map((s) => ({ label: s.status, value: s.id }))}
            selectedItems={selectedStatuses || []}
            setSelectedItems={setSelectedStatuses}
            tooltip="Статусы"
          />
        )}
        {
          type === INCOMING && (
            <CheckboxList
              appearance="default"
              intent="none"
              icon={ForkIcon}
              options={channels.map((c) => ({ label: c.description, value: c.id }))}
              selectedItems={selectedChannels || []}
              setSelectedItems={setSelectedChannels}
              tooltip="Каналы"
            />
          )
        }
        {
          type === CUT && (
            <>
              <Tooltip content="Режим отображения" position={Position.TOP}>
                <IconButton icon={ControlIcon} onClick={toggleView} />
              </Tooltip>
              <CheckboxList
                appearance="default"
                intent="none"
                options={programs.map((s) => ({ label: s.name, value: s.id }))}
                selectedItems={selectedPrograms || []}
                setSelectedItems={setSelectedPrograms}
                icon={ManualIcon}
                tooltip="Программы наблюдений"
              />
              <CheckboxList
                appearance="default"
                intent="none"
                options={formats.map((s) => ({ label: s.description, value: s.id }))}
                selectedItems={selectedFormats || []}
                setSelectedItems={setSelectedFormats}
                icon={InboxIcon}
                tooltip="Форматы"
              />
            </>
          )
        }
        {
          isArchive && (
          <SearchingOnStationAndDatasource
            datasource={selectedDatasource}
            datasources={datasources}
            setDatasource={setSelectedDatasource}
            stations={stations}
            setStation={setSelectedStation}
            station={selectedStation}
          />
          )
        }
        {type === CUT ?
            <Tooltip content={"Выгрузить вложения"}>
              <IconButton
                  icon={DownloadIcon}
                  appearance="primary"
                  intent="success"
                  onClick={onDownloadClick}
              />
            </Tooltip>
             : <></>
        }

        <IconButton
          icon={SearchIcon}
          appearance="primary"
          intent="success"
          onClick={onSearchClick}
        />
      </Pane>
    </Pane>
  );
}
